import React, { useEffect, useMemo, useState } from 'react';
import {
  AdminPortfolioHoldingDto,
  AdminPortfolioHoldingUpsertRequest,
  AdminUserDto,
  adminApi,
  isUnauthorized,
} from '../../services/adminApi';
import { getAdminTheme } from './adminTheme';

interface AdminPortfoliosScreenProps {
  isDarkMode: boolean;
  token: string;
  onUnauthorized: () => void;
}

const AdminPortfoliosScreen: React.FC<AdminPortfoliosScreenProps> = ({ isDarkMode, token, onUnauthorized }) => {
  const theme = useMemo(() => getAdminTheme(isDarkMode), [isDarkMode]);

  const colors = useMemo(
    () => ({
      card: theme.cardBg,
      border: theme.border,
      text: theme.text,
      subText: theme.subText,
      hover: theme.hoverBg,
      inputBg: theme.inputBg,
      primary: theme.primary,
      danger: theme.danger,
      radius: theme.radius,
    }),
    [theme]
  );

  const [items, setItems] = useState<AdminPortfolioHoldingDto[] | null>(null);
  const [users, setUsers] = useState<AdminUserDto[] | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(() => new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [filters, setFilters] = useState({
    username: '',
    category: '',
    symbol: '',
    status: '',
    closed: 'all' as 'all' | 'open' | 'closed',
    sellAlert: 'all' as 'all' | 'has' | 'none',
  });

  const [isUpsertOpen, setIsUpsertOpen] = useState(false);
  const [upserting, setUpserting] = useState(false);
  const [upsertError, setUpsertError] = useState<string | null>(null);
  const [draft, setDraft] = useState<AdminPortfolioHoldingUpsertRequest>({
    userId: 0,
    symbol: '',
    shares: 1,
    buyPrice: 1,
    currentPrice: null,
    buyPriceAlert: null,
    sellPriceAlert: null,
    status: 'Not Reached',
    isClosed: false,
  });

  const inputStyle: React.CSSProperties = {
    width: '100%',
    height: 34,
    borderRadius: colors.radius,
    border: `1px solid ${colors.border}`,
    backgroundColor: colors.inputBg,
    padding: '0 10px',
    color: colors.text,
    outline: 'none',
    boxSizing: 'border-box',
    fontSize: 13,
    fontWeight: 700,
  };

  const buttonStyle = (variant: 'primary' | 'neutral' | 'danger', disabled?: boolean): React.CSSProperties => {
    const base: React.CSSProperties = {
      height: 34,
      padding: '0 12px',
      borderRadius: colors.radius,
      border: `1px solid ${colors.border}`,
      backgroundColor: colors.card,
      color: colors.text,
      cursor: disabled ? 'not-allowed' : 'pointer',
      fontSize: 13,
      fontWeight: 800,
      opacity: disabled ? 0.6 : 1,
      whiteSpace: 'nowrap',
    };

    if (variant === 'primary') return { ...base, border: 'none', backgroundColor: colors.primary, color: '#fff' };
    if (variant === 'danger') return { ...base, border: 'none', backgroundColor: colors.danger, color: '#fff' };
    return base;
  };

  const normalizeSymbol = (v: unknown): string => (v ?? '').toString().trim().toUpperCase();

  const refresh = async (opts?: { clearSelection?: boolean }) => {
    setLoading(true);
    setError(null);
    try {
      const list = await adminApi.listPortfolioHoldings(token, {
        username: filters.username.trim() || undefined,
        category: filters.category.trim() || undefined,
        symbol: filters.symbol.trim() || undefined,
      });
      setItems(list);
      if (opts?.clearSelection) setSelectedIds(new Set());
    } catch (e) {
      if (isUnauthorized(e)) {
        onUnauthorized();
        return;
      }
      setError(e instanceof Error ? e.message : 'Failed loading portfolio holdings');
      setItems(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refresh({ clearSelection: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        const list = await adminApi.listUsers(token);
        if (cancelled) return;
        setUsers(list);
      } catch (e) {
        if (isUnauthorized(e)) {
          onUnauthorized();
          return;
        }
        if (cancelled) return;
        setUsers(null);
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [token, onUnauthorized]);

  const userGroupByUserId = useMemo(() => {
    const map = new Map<number, string>();
    for (const u of users ?? []) {
      map.set(u.id, (u.userGroup ?? '').toString().trim());
    }
    return map;
  }, [users]);

  const filteredItems = useMemo(() => {
    const list = items ?? [];

    const symQ = normalizeSymbol(filters.symbol);
    const usernameQ = (filters.username ?? '').toString().trim().toLowerCase();
    const categoryQ = (filters.category ?? '').toString().trim().toLowerCase();
    const statusQ = (filters.status ?? '').toString().trim().toLowerCase();

    return list.filter((x) => {
      if (usernameQ) {
        const u = (x.username ?? '').toString().trim().toLowerCase();
        if (!u.includes(usernameQ)) return false;
      }

      if (categoryQ) {
        const raw = userGroupByUserId.get(x.userId);
        if (raw != null) {
          const c = raw.toString().trim().toLowerCase();
          if (!c.includes(categoryQ)) return false;
        }
      }

      if (symQ) {
        if (!normalizeSymbol(x.symbol).includes(symQ)) return false;
      }

      if (statusQ) {
        const s = (x.status ?? '').toString().trim().toLowerCase();
        if (!s.includes(statusQ)) return false;
      }

      if (filters.closed === 'open' && Boolean(x.isClosed)) return false;
      if (filters.closed === 'closed' && !Boolean(x.isClosed)) return false;

      const hasSellAlert = x.sellPriceAlert != null && Number.isFinite(Number(x.sellPriceAlert));
      if (filters.sellAlert === 'has' && !hasSellAlert) return false;
      if (filters.sellAlert === 'none' && hasSellAlert) return false;

      return true;
    });
  }, [filters.category, filters.closed, filters.sellAlert, filters.status, filters.symbol, filters.username, items, userGroupByUserId]);

  const usernameOptions = useMemo(() => {
    const list = (users ?? [])
      .map((u) => (u.username ?? '').toString().trim())
      .filter((u) => u.length > 0);
    return Array.from(new Set(list)).sort((a, b) => a.localeCompare(b));
  }, [users]);

  const categoryOptions = useMemo(() => {
    const list = (users ?? [])
      .map((u) => (u.userGroup ?? '').toString().trim())
      .filter((c) => c.length > 0);
    return Array.from(new Set(list)).sort((a, b) => a.localeCompare(b));
  }, [users]);

  const statusOptions = useMemo(() => {
    const list = (items ?? [])
      .map((x) => (x.status ?? '').toString().trim())
      .filter((s) => s.length > 0);
    return Array.from(new Set(list)).sort((a, b) => a.localeCompare(b));
  }, [items]);

  type ColumnDef = {
    key: string;
    title: string;
    width: number;
    render: (row: AdminPortfolioHoldingDto) => React.ReactNode;
  };

  const renderCellValue = (value: unknown): React.ReactNode => (value == null || value === '' ? '—' : String(value));

  const formatCurrency = (value: number | null | undefined): string => {
    if (value == null) return '—';
    const n = Number(value);
    if (!Number.isFinite(n)) return '—';
    return n.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 2 });
  };

  const formatPct = (value: number | null | undefined): string => {
    if (value == null) return '—';
    const n = Number(value);
    if (!Number.isFinite(n)) return '—';
    const rounded = Math.round(n * 100) / 100;
    const safe = Object.is(rounded, -0) ? 0 : rounded;
    const sign = safe > 0 ? '+' : '';
    return `${sign}${safe.toFixed(2)}%`;
  };

  const computeCost = (row: AdminPortfolioHoldingDto): number | null => {
    const shares = Number(row.shares);
    const buyPrice = Number(row.buyPrice);
    if (!Number.isFinite(shares) || !Number.isFinite(buyPrice)) return null;
    return shares * buyPrice;
  };

  const computeMarketValue = (row: AdminPortfolioHoldingDto): number | null => {
    const shares = Number(row.shares);
    const price = row.currentPrice == null ? NaN : Number(row.currentPrice);
    if (!Number.isFinite(shares) || !Number.isFinite(price)) return null;
    return shares * price;
  };

  const computeProfitLoss = (row: AdminPortfolioHoldingDto): number | null => {
    const cost = computeCost(row);
    const marketValue = computeMarketValue(row);
    if (cost == null || marketValue == null) return null;
    return marketValue - cost;
  };

  const computeChangePct = (row: AdminPortfolioHoldingDto): number | null => {
    const buyPrice = Number(row.buyPrice);
    const price = row.currentPrice == null ? NaN : Number(row.currentPrice);
    if (!Number.isFinite(buyPrice) || buyPrice <= 0 || !Number.isFinite(price)) return null;
    return ((price - buyPrice) / buyPrice) * 100;
  };

  const columns = useMemo<ColumnDef[]>(() => {
    const selectCol: ColumnDef = {
      key: 'select',
      title: '',
      width: 48,
      render: (row) => {
        const checked = selectedIds.has(row.id);
        return (
          <input
            type="checkbox"
            checked={checked}
            onChange={(e) => {
              const next = new Set(selectedIds);
              if (e.target.checked) next.add(row.id);
              else next.delete(row.id);
              setSelectedIds(next);
            }}
          />
        );
      },
    };

    const fixed: ColumnDef[] = [
      selectCol,
      { key: 'id', title: 'ID', width: 90, render: (row) => row.id },
      { key: 'userId', title: 'User ID', width: 110, render: (row) => row.userId },
      { key: 'username', title: 'Username', width: 160, render: (row) => row.username ?? '—' },
      { key: 'portfolioId', title: 'Portfolio ID', width: 120, render: (row) => row.portfolioId },
      { key: 'portfolioName', title: 'Portfolio', width: 200, render: (row) => row.portfolioName ?? '—' },
      { key: 'updatedAtUtc', title: 'Updated', width: 200, render: (row) => renderCellValue(row.updatedAtUtc) },
    ];

    const openOnly: ColumnDef[] = [
      { key: 'symbol', title: 'Stock Symbol', width: 120, render: (row) => row.symbol },
      { key: 'buyDateUtc', title: 'Buy Date', width: 200, render: (row) => renderCellValue(row.buyDateUtc) },
      { key: 'buyPrice', title: 'Buy Price', width: 120, render: (row) => formatCurrency(Number(row.buyPrice)) },
      { key: 'currentPrice', title: 'Current Price', width: 140, render: (row) => formatCurrency(row.currentPrice == null ? null : Number(row.currentPrice)) },
      { key: 'changePct', title: 'Change (%)', width: 120, render: (row) => formatPct(computeChangePct(row)) },
      { key: 'shares', title: 'Shares', width: 110, render: (row) => row.shares },
      { key: 'cost', title: 'Cost', width: 130, render: (row) => formatCurrency(computeCost(row)) },
      { key: 'marketValue', title: 'Market Value', width: 140, render: (row) => formatCurrency(computeMarketValue(row)) },
      { key: 'profitLoss', title: 'Profit/Loss', width: 140, render: (row) => formatCurrency(computeProfitLoss(row)) },
      { key: 'buyPriceAlert', title: 'Buy Price Alert', width: 140, render: (row) => formatCurrency(row.buyPriceAlert == null ? null : Number(row.buyPriceAlert)) },
      { key: 'sellPriceAlert', title: 'Sell Price Alert', width: 140, render: (row) => formatCurrency(row.sellPriceAlert == null ? null : Number(row.sellPriceAlert)) },
      { key: 'status', title: 'Alert Status', width: 150, render: (row) => row.status },
    ];

    const closedOnly: ColumnDef[] = [
      { key: 'symbol', title: 'Stock Symbol', width: 120, render: (row) => row.symbol },
      { key: 'buyDateUtc', title: 'Buy Date', width: 200, render: (row) => renderCellValue(row.buyDateUtc) },
      { key: 'shares', title: 'Shares Sold', width: 130, render: (row) => row.shares },
      { key: 'buyPrice', title: 'Buy Price', width: 120, render: (row) => formatCurrency(Number(row.buyPrice)) },
      { key: 'sellDateUtc', title: 'Sell Date', width: 200, render: (row) => renderCellValue(row.sellDateUtc) },
      { key: 'sellPrice', title: 'Sell Price', width: 140, render: (row) => formatCurrency(row.currentPrice == null ? null : Number(row.currentPrice)) },
      { key: 'changePct', title: 'Change (%)', width: 120, render: (row) => formatPct(computeChangePct(row)) },
      { key: 'profitLoss', title: 'Profit/Loss ($)', width: 140, render: (row) => formatCurrency(computeProfitLoss(row)) },
    ];

    if (filters.closed === 'open') return [...fixed, ...openOnly];
    if (filters.closed === 'closed') return [...fixed, ...closedOnly];

    return [
      ...fixed,
      ...openOnly,
      { key: 'sellDateUtc', title: 'Sell Date', width: 200, render: (row) => renderCellValue(row.sellDateUtc) },
    ];
  }, [filters.closed, selectedIds]);

  const cellStyle: React.CSSProperties = {
    padding: '10px 12px',
    borderBottom: `1px solid ${colors.border}`,
    fontSize: 13,
    color: colors.text,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  };

  const headerCellStyle: React.CSSProperties = {
    ...cellStyle,
    position: 'sticky',
    top: 0,
    backgroundColor: isDarkMode ? 'rgba(255,255,255,0.03)' : '#f9fafb',
    zIndex: 1,
    fontSize: 12,
    fontWeight: 900,
    color: colors.subText,
    textAlign: 'left',
  };

  const parseNumberOrNull = (raw: string): number | null => {
    const t = raw.trim();
    if (!t) return null;
    const n = Number(t);
    return Number.isFinite(n) ? n : null;
  };

  const openCreate = () => {
    setUpsertError(null);
    setDraft({
      userId: 0,
      portfolioId: null,
      portfolioName: null,
      symbol: '',
      shares: 1,
      buyPrice: 1,
      currentPrice: null,
      buyPriceAlert: null,
      sellPriceAlert: null,
      status: 'Not Reached',
      isClosed: false,
    });
    setIsUpsertOpen(true);
  };

  const openEdit = (row: AdminPortfolioHoldingDto) => {
    setUpsertError(null);
    setDraft({
      id: row.id,
      userId: row.userId,
      portfolioId: row.portfolioId,
      portfolioName: row.portfolioName ?? null,
      symbol: row.symbol,
      shares: Number(row.shares),
      buyPrice: Number(row.buyPrice),
      currentPrice: row.currentPrice == null ? null : Number(row.currentPrice),
      buyPriceAlert: row.buyPriceAlert == null ? null : Number(row.buyPriceAlert),
      sellPriceAlert: row.sellPriceAlert == null ? null : Number(row.sellPriceAlert),
      status: row.status,
      isClosed: Boolean(row.isClosed),
    });
    setIsUpsertOpen(true);
  };

  const deleteSelected = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;

    const ok = window.confirm(`Delete ${ids.length} selected holding(s)?`);
    if (!ok) return;

    setLoading(true);
    setError(null);
    try {
      for (const id of ids) {
        // eslint-disable-next-line no-await-in-loop
        await adminApi.deletePortfolioHolding(token, id);
      }
      await refresh({ clearSelection: true });
    } catch (e) {
      if (isUnauthorized(e)) {
        onUnauthorized();
        return;
      }
      setError(e instanceof Error ? e.message : 'Failed deleting holdings');
    } finally {
      setLoading(false);
    }
  };

  const upsert = async () => {
    setUpsertError(null);

    const userId = Number(draft.userId);
    if (!Number.isFinite(userId) || userId <= 0) {
      setUpsertError('User is required');
      return;
    }

    const symbol = normalizeSymbol(draft.symbol);
    if (!symbol) {
      setUpsertError('Symbol is required');
      return;
    }

    const shares = Number(draft.shares);
    if (!Number.isFinite(shares) || shares <= 0) {
      setUpsertError('Shares must be > 0');
      return;
    }

    const buyPrice = Number(draft.buyPrice);
    if (!Number.isFinite(buyPrice) || buyPrice <= 0) {
      setUpsertError('Buy Price must be > 0');
      return;
    }

    const portfolioIdRaw = draft.portfolioId == null ? null : Number(draft.portfolioId);
    const portfolioId = Number.isFinite(portfolioIdRaw) && (portfolioIdRaw as number) > 0 ? (portfolioIdRaw as number) : null;
    const portfolioName = (draft.portfolioName ?? '').toString().trim();

    setUpserting(true);
    try {
      await adminApi.upsertPortfolioHolding(token, {
        ...draft,
        userId,
        portfolioId,
        portfolioName: portfolioName.length ? portfolioName : null,
        symbol,
        shares,
        buyPrice,
        currentPrice: draft.currentPrice == null ? null : Number(draft.currentPrice),
        buyPriceAlert: draft.buyPriceAlert == null ? null : Number(draft.buyPriceAlert),
        sellPriceAlert: draft.sellPriceAlert == null ? null : Number(draft.sellPriceAlert),
      });
      setIsUpsertOpen(false);
      await refresh({ clearSelection: true });
    } catch (e) {
      if (isUnauthorized(e)) {
        onUnauthorized();
        return;
      }
      setUpsertError(e instanceof Error ? e.message : 'Failed saving holding');
    } finally {
      setUpserting(false);
    }
  };

  return (
    <div
      style={{
        backgroundColor: colors.card,
        border: `1px solid ${colors.border}`,
        borderRadius: colors.radius,
        padding: 14,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 900, color: colors.text, marginBottom: 2 }}>Portfolios</div>
          <div style={{ fontSize: 13, fontWeight: 700, color: colors.subText }}>View & manage portfolio holdings per user</div>
        </div>

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button type="button" style={buttonStyle('neutral')} onClick={() => void refresh({ clearSelection: true })} disabled={loading}>
            {loading ? 'Loading…' : 'Refresh'}
          </button>
          <button type="button" style={buttonStyle('danger', loading || selectedIds.size === 0)} disabled={loading || selectedIds.size === 0} onClick={() => void deleteSelected()}>
            Delete Selected
          </button>
          <button type="button" style={buttonStyle('primary', loading)} disabled={loading} onClick={openCreate}>
            Add Holding
          </button>
        </div>
      </div>

      {error ? (
        <div style={{ padding: 12, marginBottom: 12, borderRadius: colors.radius, border: `1px solid ${colors.border}`, color: colors.danger, fontWeight: 800, fontSize: 13 }}>
          {error}
        </div>
      ) : null}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 12, fontWeight: 900, color: colors.subText, marginBottom: 6 }}>Username</div>
          <input
            list="admin-portfolios-usernames"
            value={filters.username}
            onChange={(e) => setFilters((p) => ({ ...p, username: e.target.value }))}
            placeholder="Filter by username"
            style={inputStyle}
          />
          <datalist id="admin-portfolios-usernames">{usernameOptions.map((u) => <option key={u} value={u} />)}</datalist>
        </div>

        <div>
          <div style={{ fontSize: 12, fontWeight: 900, color: colors.subText, marginBottom: 6 }}>Category</div>
          <input
            list="admin-portfolios-categories"
            value={filters.category}
            onChange={(e) => setFilters((p) => ({ ...p, category: e.target.value }))}
            placeholder="Filter by category"
            style={inputStyle}
          />
          <datalist id="admin-portfolios-categories">{categoryOptions.map((c) => <option key={c} value={c} />)}</datalist>
        </div>

        <div>
          <div style={{ fontSize: 12, fontWeight: 900, color: colors.subText, marginBottom: 6 }}>Symbol</div>
          <input value={filters.symbol} onChange={(e) => setFilters((p) => ({ ...p, symbol: e.target.value }))} placeholder="Filter by symbol" style={inputStyle} />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 12, fontWeight: 900, color: colors.subText, marginBottom: 6 }}>Status</div>
          <input
            list="admin-portfolios-statuses"
            value={filters.status}
            onChange={(e) => setFilters((p) => ({ ...p, status: e.target.value }))}
            placeholder="Filter by status"
            style={inputStyle}
          />
          <datalist id="admin-portfolios-statuses">{statusOptions.map((s) => <option key={s} value={s} />)}</datalist>
        </div>

        <div>
          <div style={{ fontSize: 12, fontWeight: 900, color: colors.subText, marginBottom: 6 }}>Closed</div>
          <select value={filters.closed} onChange={(e) => setFilters((p) => ({ ...p, closed: e.target.value as 'all' | 'open' | 'closed' }))} style={inputStyle}>
            <option value="all">All</option>
            <option value="open">Open</option>
            <option value="closed">Closed</option>
          </select>
        </div>

        <div>
          <div style={{ fontSize: 12, fontWeight: 900, color: colors.subText, marginBottom: 6 }}>Sell Alert</div>
          <select value={filters.sellAlert} onChange={(e) => setFilters((p) => ({ ...p, sellAlert: e.target.value as 'all' | 'has' | 'none' }))} style={inputStyle}>
            <option value="all">All</option>
            <option value="has">Has alert</option>
            <option value="none">No alert</option>
          </select>
        </div>
      </div>

      <div style={{ overflow: 'auto', border: `1px solid ${colors.border}`, borderRadius: colors.radius }}>
        <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, minWidth: 1450 }}>
          <thead>
            <tr>
              {columns.map((c) => (
                <th key={c.key} style={{ ...headerCellStyle, width: c.width }}>{c.title}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredItems.map((row) => {
              return (
                <tr
                  key={row.id}
                  onDoubleClick={() => openEdit(row)}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLTableRowElement).style.backgroundColor = colors.hover;
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLTableRowElement).style.backgroundColor = 'transparent';
                  }}
                >
                  {columns.map((col) => (
                    <td key={col.key} style={cellStyle}>{col.render(row)}</td>
                  ))}
                </tr>
              );
            })}

            {!loading && (filteredItems.length === 0) ? (
              <tr>
                <td style={{ ...cellStyle, color: colors.subText, fontWeight: 800 }} colSpan={columns.length}>
                  No holdings found.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      {isUpsertOpen ? (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
            zIndex: 9999,
          }}
          onMouseDown={() => {
            if (!upserting) setIsUpsertOpen(false);
          }}
        >
          <div
            style={{
              width: 'min(560px, 100%)',
              backgroundColor: colors.card,
              border: `1px solid ${colors.border}`,
              borderRadius: colors.radius,
              padding: 14,
              color: colors.text,
            }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
              <div style={{ fontSize: 16, fontWeight: 900 }}>Upsert Holding</div>
              <button type="button" style={buttonStyle('neutral', upserting)} disabled={upserting} onClick={() => setIsUpsertOpen(false)}>
                Close
              </button>
            </div>

            {upsertError ? (
              <div style={{ padding: 10, borderRadius: colors.radius, border: `1px solid ${colors.border}`, color: colors.danger, fontWeight: 800, fontSize: 13, marginBottom: 10 }}>
                {upsertError}
              </div>
            ) : null}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 900, color: colors.subText, marginBottom: 6 }}>User</div>
                <select
                  value={String(draft.userId ?? 0)}
                  onChange={(e) => setDraft((p) => ({ ...p, userId: Number(e.target.value) }))}
                  style={inputStyle}
                >
                  <option value="0">Select user</option>
                  {(users ?? []).filter((u) => u.isDeleted !== true).map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.username} (#{u.id})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <div style={{ fontSize: 12, fontWeight: 900, color: colors.subText, marginBottom: 6 }}>Symbol</div>
                <input value={draft.symbol ?? ''} onChange={(e) => setDraft((p) => ({ ...p, symbol: e.target.value }))} style={inputStyle} placeholder="AAPL" />
              </div>

              <div>
                <div style={{ fontSize: 12, fontWeight: 900, color: colors.subText, marginBottom: 6 }}>Portfolio ID (optional)</div>
                <input
                  value={draft.portfolioId == null ? '' : String(draft.portfolioId)}
                  onChange={(e) => setDraft((p) => ({ ...p, portfolioId: parseNumberOrNull(e.target.value) }))}
                  style={inputStyle}
                  inputMode="numeric"
                  placeholder="(leave blank for default)"
                />
              </div>

              <div>
                <div style={{ fontSize: 12, fontWeight: 900, color: colors.subText, marginBottom: 6 }}>Portfolio Name (optional)</div>
                <input
                  value={draft.portfolioName ?? ''}
                  onChange={(e) => setDraft((p) => ({ ...p, portfolioName: e.target.value }))}
                  style={inputStyle}
                  placeholder="Portfolio Holdings"
                />
              </div>

              <div>
                <div style={{ fontSize: 12, fontWeight: 900, color: colors.subText, marginBottom: 6 }}>Shares</div>
                <input
                  value={String(draft.shares ?? '')}
                  onChange={(e) => setDraft((p) => ({ ...p, shares: parseNumberOrNull(e.target.value) ?? 0 }))}
                  style={inputStyle}
                  inputMode="decimal"
                />
              </div>

              <div>
                <div style={{ fontSize: 12, fontWeight: 900, color: colors.subText, marginBottom: 6 }}>Buy Price</div>
                <input
                  value={String(draft.buyPrice ?? '')}
                  onChange={(e) => setDraft((p) => ({ ...p, buyPrice: parseNumberOrNull(e.target.value) ?? 0 }))}
                  style={inputStyle}
                  inputMode="decimal"
                />
              </div>

              <div>
                <div style={{ fontSize: 12, fontWeight: 900, color: colors.subText, marginBottom: 6 }}>Current Price</div>
                <input
                  value={draft.currentPrice == null ? '' : String(draft.currentPrice)}
                  onChange={(e) => setDraft((p) => ({ ...p, currentPrice: parseNumberOrNull(e.target.value) }))}
                  style={inputStyle}
                  inputMode="decimal"
                />
              </div>

              <div>
                <div style={{ fontSize: 12, fontWeight: 900, color: colors.subText, marginBottom: 6 }}>Status</div>
                <select value={draft.status ?? 'Not Reached'} onChange={(e) => setDraft((p) => ({ ...p, status: e.target.value }))} style={inputStyle}>
                  <option value="Not Reached">Not Reached</option>
                  <option value="Buy Triggered">Buy Triggered</option>
                  <option value="Sell Triggered">Sell Triggered</option>
                </select>
              </div>

              <div>
                <div style={{ fontSize: 12, fontWeight: 900, color: colors.subText, marginBottom: 6 }}>Buy Alert</div>
                <input
                  value={draft.buyPriceAlert == null ? '' : String(draft.buyPriceAlert)}
                  onChange={(e) => setDraft((p) => ({ ...p, buyPriceAlert: parseNumberOrNull(e.target.value) }))}
                  style={inputStyle}
                  inputMode="decimal"
                />
              </div>

              <div>
                <div style={{ fontSize: 12, fontWeight: 900, color: colors.subText, marginBottom: 6 }}>Sell Alert</div>
                <input
                  value={draft.sellPriceAlert == null ? '' : String(draft.sellPriceAlert)}
                  onChange={(e) => setDraft((p) => ({ ...p, sellPriceAlert: parseNumberOrNull(e.target.value) }))}
                  style={inputStyle}
                  inputMode="decimal"
                />
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 10, paddingTop: 18 }}>
                <input
                  type="checkbox"
                  checked={Boolean(draft.isClosed)}
                  onChange={(e) => setDraft((p) => ({ ...p, isClosed: e.target.checked }))}
                />
                <div style={{ fontSize: 13, fontWeight: 800, color: colors.text }}>Closed</div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 12 }}>
              <button type="button" style={buttonStyle('neutral', upserting)} disabled={upserting} onClick={() => setIsUpsertOpen(false)}>
                Cancel
              </button>
              <button type="button" style={buttonStyle('primary', upserting)} disabled={upserting} onClick={() => void upsert()}>
                {upserting ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default AdminPortfoliosScreen;
